import { ColDef, ICellRendererParams } from 'ag-grid-community';

import DealIdRenderer from 'components/common/DealIdRenderer';
import NumberRenderer from 'components/common/NumberRenderer';
import { DealItemData } from 'components/common/types/DealItem';

import SkeletonCell from 'lib/SkeletonCell/SkeletonCell';

import { percentageCellRenderer } from 'utils/cellRenderer';
import { formatDDMMYYYYDate } from 'utils/dateFormatUtil';
import { getDealTypeLabel } from 'utils/getDealTypeLabel';

import copyableCellRenderer from 'components/common/CopyToClipboardCellRenderer/utils';
import BidsRenderer from './BidsRenderer';
import ImpressionsRenderer from './ImpressionsRenderer';
import { StatusRenderer } from '../../common/StatusRenderer/StatusRenderer';
import OutOfChargeRenderer from './OutOfChargeRenderer';
import ColumnHeader from './ColumnHeader/ColumnHeader';
import DeliveryStatsRenderer from './DeliveryStatsRenderer';
import ServicesRenderer from './ServicesRenderer';

const dealTableColumnDefs = ({
  localeCode,
  hasRevenueViewPermission,
  hasOutOfChargeEnabled,
  hasQuickDeliveryStatsEnabled,
  onPieChartClick,
  configEnabledLineLevelTrading,
  hasServicesViewEnabled,
}: {
  localeCode: string;
  hasRevenueViewPermission: boolean;
  hasOutOfChargeEnabled: boolean;
  hasQuickDeliveryStatsEnabled: boolean;
  onPieChartClick: (data: DealItemData) => void;
  configEnabledLineLevelTrading: boolean;
  hasServicesViewEnabled: boolean;
}): ColDef[] => [
  {
    colId: 'actions',
    field: 'dealId',
    headerComponent: ColumnHeader,
    headerName: 'ID',
    headerTooltip: '',
    width: 190,
    resizable: false,
    sortable: false,
    cellRenderer: (params: ICellRendererParams) => {
      if (params.data?.isSkeletonRow) return <SkeletonCell width="w-32" />;
      return DealIdRenderer({
        data: {
          dealId: params.value,
          isChildRow: params.data?.isChildRow,
          enableLineLevelTrading: params.data?.enableLineLevelTrading,
        },
        preTruncateId: true,
        configEnabledLineLevelTrading,
      });
    },
  },
  {
    field: 'dealTypeCode',
    headerComponent: ColumnHeader,
    headerName: 'Deal type',
    headerTooltip: 'Deal types',
    width: 170,
    resizable: true,
    sortable: true,
    cellRenderer: (params: ICellRendererParams) => {
      if (params.data?.isSkeletonRow) return <SkeletonCell width="w-28" />;
      return getDealTypeLabel(params.value);
    },
  },
  {
    colId: 'status',
    field: 'bookingStatusCode',
    headerComponent: ColumnHeader,
    headerName: 'Status',
    headerTooltip: 'Status',
    resizable: true,
    sortable: false,
    width: 120,
    cellRenderer: (params: ICellRendererParams) => {
      if (params.data?.isSkeletonRow) return <SkeletonCell width="w-14" />;
      return StatusRenderer({ campaignStatus: params?.value });
    },
  },
  {
    field: 'startDate',
    headerName: 'Start date',
    headerTooltip: '',
    resizable: false,
    sortable: true,
    suppressMenu: true,
    width: 120,
    cellRenderer: ({ data }: ICellRendererParams) => {
      if (data?.isSkeletonRow) return <SkeletonCell width="w-12" />;
      return <span>{formatDDMMYYYYDate(data?.startDate, localeCode)}</span>;
    },
  },
  {
    field: 'endDate',
    headerName: 'End date',
    headerTooltip: '',
    resizable: false,
    sortable: true,
    suppressMenu: true,
    width: 120,
    cellRenderer: ({ data }: ICellRendererParams) => {
      if (data?.isSkeletonRow) return <SkeletonCell width="w-12" />;
      return <span>{formatDDMMYYYYDate(data?.endDate, localeCode)}</span>;
    },
  },
  {
    field: 'dspName',
    headerComponent: ColumnHeader,
    headerName: 'DSP',
    headerTooltip: 'DSP name',
    resizable: true,
    sortable: true,
    suppressMenu: true,
    width: 180,
    cellRenderer: (params: ICellRendererParams) => {
      if (params.data?.isSkeletonRow) return <SkeletonCell width="w-24" />;
      return params.value ?? '';
    },
  },
  {
    field: 'cpm',
    headerComponent: ColumnHeader,
    headerName: 'CPM',
    headerTooltip: 'CPM',
    sortable: true,
    suppressMenu: true,
    width: 120,
    cellRenderer: ({ value, data }: ICellRendererParams) => {
      if (data?.isSkeletonRow) return <SkeletonCell width="w-12" />;
      const cpmValue = data?.isChildRow ? (data?.floorCPM ?? value) : value;
      return NumberRenderer({ value: cpmValue, localeCode });
    },
  },
  {
    field: 'Services',
    headerComponent: ColumnHeader,
    headerName: 'Services',
    headerTooltip: 'Approved/Pending services',
    hide: !hasServicesViewEnabled,
    sortable: false,
    suppressMenu: true,
    width: 120,
    cellRenderer: ({ data }: ICellRendererParams) => <ServicesRenderer data={data} />,
  },
  {
    colId: 'Delivery stats',
    field: 'Delivery stats',
    headerName: 'Delivery stats',
    headerTooltip: 'Delivery stats',
    hide: !hasQuickDeliveryStatsEnabled,
    minWidth: 430,
    sortable: false,
    suppressMenu: true,
    cellRenderer: ({ data }: ICellRendererParams) => {
      if (data?.isSkeletonRow) return <SkeletonCell width="w-12" />;
      return <DeliveryStatsRenderer data={data} />;
    },
  },
  {
    colId: 'bids',
    field: 'bids',
    headerName: 'Bids',
    headerTooltip: 'Bids',
    hide: hasQuickDeliveryStatsEnabled,
    minWidth: 430,
    sortable: false,
    suppressMenu: true,
    cellRenderer: (params: ICellRendererParams) => {
      if (params.data?.isChildRow) return null;
      return BidsRenderer({ ...params, onClick: onPieChartClick });
    },
  },
  {
    field: 'bids.fillRate',
    headerName: 'Fill rate',
    headerTooltip: 'Fill rate',
    hide: hasQuickDeliveryStatsEnabled,
    sortable: false,
    suppressMenu: true,
    width: 120,
    cellRenderer: (params: ICellRendererParams) => {
      if (params.data?.isChildRow) return '';
      return percentageCellRenderer(params, localeCode);
    },
  },
  {
    field: 'bids.revenue',
    headerName: 'Revenue',
    headerTooltip: 'Revenue',
    hide: !hasRevenueViewPermission || hasQuickDeliveryStatsEnabled,
    sortable: false,
    suppressMenu: true,
    width: 120,
    cellRenderer: ({ value, data }: ICellRendererParams) => {
      if (data?.isChildRow) return '';
      return NumberRenderer({ value, localeCode });
    },
  },
  {
    field: 'advertiserName',
    headerComponent: ColumnHeader,
    headerName: 'Advertiser',
    headerTooltip: 'Advertiser',
    resizable: true,
    sortable: true,
    width: 200,
    cellRenderer: copyableCellRenderer(),
  },
  {
    field: 'brandName',
    headerComponent: ColumnHeader,
    headerName: 'Brand',
    headerTooltip: 'Brand',
    resizable: true,
    sortable: true,
    width: 200,
    cellRenderer: copyableCellRenderer(),
  },
  {
    field: 'productCategoryName',
    headerComponent: ColumnHeader,
    headerName: 'Category',
    headerTooltip: 'Category',
    resizable: true,
    sortable: true,
    width: 180,
    cellRenderer: (params: ICellRendererParams) => {
      if (params.data?.isSkeletonRow) return <SkeletonCell width="w-24" />;
      return params.value ?? '';
    },
  },
  {
    field: 'agencyName',
    headerComponent: ColumnHeader,
    headerName: 'Agency',
    headerTooltip: 'Agency',
    resizable: true,
    sortable: true,
    width: 180,
    cellRenderer: (params: ICellRendererParams) => {
      if (params.data?.isSkeletonRow) return <SkeletonCell width="w-24" />;
      return params.value ?? '';
    },
  },
  {
    colId: 'impressions',
    field: 'allocatedImpressions',
    headerName: 'IMPRESSIONS',
    headerTooltip: 'IMPRESSIONS',
    hide: true,
    minWidth: 420,
    sortable: false,
    suppressMenu: true,
    cellRenderer: (params: ICellRendererParams) => {
      if (params.data?.isChildRow) return null;
      return ImpressionsRenderer({ ...params, onClick: onPieChartClick });
    },
  },
  {
    field: 'remainingImpressions',
    headerName: 'Remaining Impressions',
    headerTooltip: 'Remaining Impressions',
    hide: true,
    minWidth: 180,
    sortable: false,
    suppressMenu: true,
    cellRenderer: ({ value }: ICellRendererParams) => NumberRenderer({ value, localeCode }),
  },
  {
    colId: 'includesDischargedFrames',
    cellRenderer: OutOfChargeRenderer,
    field: 'includesDischargedFrames',
    headerName: '',
    headerTooltip: '',
    hide: !hasOutOfChargeEnabled,
    resizable: false,
    sortable: false,
    suppressMenu: true,
    width: 90,
  },
];

export default dealTableColumnDefs;
